import type { RiskProfileMetricOption, RiskProfileResponse } from '../types/riskProfile.types'
import { DAMAGE_STATE_ORDER_STRINGS } from '../utils/riskProfile.utils'

/**
 * Observed damage-state order, least to most severe. Unknown category keys
 * are appended alphabetically after the known ones rather than dropped.
 */

const compareCategoryKeysBySeverity = (a: string, b: string): number => {
  const orderA = DAMAGE_STATE_ORDER_STRINGS.indexOf(a)
  const orderB = DAMAGE_STATE_ORDER_STRINGS.indexOf(b)
  if (orderA !== -1 && orderB !== -1) return orderA - orderB
  if (orderA !== -1) return -1
  if (orderB !== -1) return 1
  return a.localeCompare(b)
}

const toDisplayLabel = (categoryKey: string): string =>
  categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1)

const UNCATEGORISED_SERIES_NAME = 'Total'

export interface AreaRankingSeries {
  /** Display label for the legend, e.g. 'Major'. */
  name: string
  /** One value per area, aligned to areaLabels order, zero-filled for sparse rows. */
  values: number[]
}

export interface AreaRankingChartData {
  /** Areas ranked by total descending, cut to topN. */
  areaLabels: string[]
  series: AreaRankingSeries[]
  /**
   * Areas dropped because their values mix incompatible units
   * (metric_type: null under metric 'quantity'). Zero under damage_cost,
   * where cross-type sums are legitimate currency.
   */
  excludedMixedUnitAreaCount: number
}

export interface MapToAreaRankingOptions {
  metricOption: RiskProfileMetricOption
  topN: number
  selectedAreas?: string[]
  useAvgPercentValue?: boolean
}

/**
 * Maps a risk profile response onto ranked stacked-bar data for the
 * area-comparison chart.
 *
 * Ranking uses the API's group_totals (never client-side summing — region
 * groups can contain nested, overlapping areas, so only API totals are
 * honest). Under metric 'quantity', groups whose total is null-typed are
 * excluded as meaningless (boat ramps + km summed) and reported via
 * excludedMixedUnitAreaCount.
 */
export const mapRiskProfileResponseToAreaRanking = (
  response: RiskProfileResponse,
  options: MapToAreaRankingOptions,
): AreaRankingChartData => {
  const labelByGroupKey = new Map(response.groups.map((group) => [group.key, group.label]))

  const excludeMixedUnitGroups =
    options.metricOption.metric === 'quantity' && options.metricOption.type !== 'percentage'
  const rankableTotals = response.group_totals.filter(
    (total) => !(excludeMixedUnitGroups && total.metric_type === null),
  )
  const excludedMixedUnitAreaCount = response.group_totals.length - rankableTotals.length
  const useAvgPercent =
    options.useAvgPercentValue === true &&
    options.metricOption.metric === 'quantity' &&
    options.metricOption.type === 'percentage'
  const rankingAttribute =
    options.metricOption.type == 'total'
      ? 'value'
      : useAvgPercent
        ? 'avg_percent_value'
        : 'percent_value'

  const { selectedAreas } = options

  // Calculate severity score for each group to use as tiebreaker when totals are equal
  const severityScoreByGroup = new Map<string, number>()
  for (const row of response.rows) {
    if (excludeMixedUnitGroups && row.metric_type === null) continue
    const categoryKey = row.category_key ?? ''
    const severityIndex = DAMAGE_STATE_ORDER_STRINGS.indexOf(categoryKey)
    const severityWeight = severityIndex !== -1 ? severityIndex : -1
    const currentScore = severityScoreByGroup.get(row.group_key) ?? 0
    // Weight severity by the value at that severity level
    severityScoreByGroup.set(row.group_key, currentScore + severityWeight * row.value)
  }

  const rankedGroupKeys =
    selectedAreas !== undefined && selectedAreas.length > 0
      ? [...rankableTotals]
          .sort((a, b) => {
            const rankDiff = (b[rankingAttribute] ?? 0) - (a[rankingAttribute] ?? 0)
            if (rankDiff !== 0) return rankDiff
            // When totals are equal, sort by severity (higher severity score = more severe)
            return (
              (severityScoreByGroup.get(b.group_key) ?? 0) -
              (severityScoreByGroup.get(a.group_key) ?? 0)
            )
          })
          .filter((total) =>
            selectedAreas.includes(labelByGroupKey.get(total.group_key) ?? total.group_key),
          )
          .map((total) => total.group_key)
      : [...rankableTotals]
          .sort((a, b) => {
            const rankDiff = (b[rankingAttribute] ?? 0) - (a[rankingAttribute] ?? 0)
            if (rankDiff !== 0) return rankDiff
            // When totals are equal, sort by severity (higher severity score = more severe)
            return (
              (severityScoreByGroup.get(b.group_key) ?? 0) -
              (severityScoreByGroup.get(a.group_key) ?? 0)
            )
          })
          .slice(0, options.topN)
          .map((total) => total.group_key)

  const groupIndexByKey = new Map(rankedGroupKeys.map((key, index) => [key, index]))

  const categoryKeys = [
    ...new Set(
      response.rows
        .filter((row) => groupIndexByKey.has(row.group_key))
        .map((row) => row.category_key ?? UNCATEGORISED_SERIES_NAME),
    ),
  ].sort(compareCategoryKeysBySeverity)

  // The total for the metric
  const totalsByGroup = new Map<string, number>(
    rankableTotals.map((groupTotal) => [groupTotal.group_key, groupTotal.total_value]),
  )

  const groupValueByKey = new Map<string, number>(
    rankableTotals.map((groupTotal) => [groupTotal.group_key, groupTotal.value]),
  )
  const avgPercentByGroup = new Map<string, number>(
    rankableTotals.map((groupTotal) => [groupTotal.group_key, groupTotal.avg_percent_value ?? 0]),
  )

  const valuesByCategory = new Map<string, number[]>(
    categoryKeys.map((categoryKey) => [
      categoryKey,
      new Array<number>(rankedGroupKeys.length).fill(0),
    ]),
  )

  for (const row of response.rows) {
    const groupIndex = groupIndexByKey.get(row.group_key)
    if (groupIndex === undefined) continue
    if (excludeMixedUnitGroups && row.metric_type === null) continue
    const categoryKey = row.category_key ?? UNCATEGORISED_SERIES_NAME
    if (options.metricOption.type === 'total')
      valuesByCategory.get(categoryKey)![groupIndex] += row.value
    else if (options.metricOption.type === 'percentage') {
      if (useAvgPercent) {
        const groupValue = groupValueByKey.get(row.group_key) ?? 0
        const avgPercent = avgPercentByGroup.get(row.group_key) ?? 0
        if (groupValue > 0)
          valuesByCategory.get(categoryKey)![groupIndex] += (row.value / groupValue) * avgPercent
      } else {
        valuesByCategory.get(categoryKey)![groupIndex] +=
          (100 * row.value) / (totalsByGroup.get(row.group_key) || 1)
      }
    }
  }

  return {
    areaLabels: rankedGroupKeys.map((key) => labelByGroupKey.get(key) ?? key),
    series: categoryKeys.map((categoryKey) => ({
      name:
        categoryKey === UNCATEGORISED_SERIES_NAME
          ? UNCATEGORISED_SERIES_NAME
          : toDisplayLabel(categoryKey),
      values: valuesByCategory.get(categoryKey) ?? [],
    })),
    excludedMixedUnitAreaCount,
  }
}

export interface SingleElementSummaryCategory {
  key: string
  label: string
  value: number
  percentOfTotal: number
}

export interface SingleElementSummary {
  label: string
  totalValue: number
  value: number
  unit: string | null
  categories: SingleElementSummaryCategory[]
}

/**
 * Maps a single-group risk profile response into a summary card shape:
 * one total figure plus a damage-state breakdown (value + % of total each).
 * Assumes exactly one group in the response — callers should check
 * data.groups.length === 1 before using this.
 */
export function mapRiskProfileResponseToSingleElementSummary(
  response: RiskProfileResponse,
  metricOption: RiskProfileMetricOption,
): SingleElementSummary | null {
  const group = response.groups[0]
  const groupTotal = response.group_totals.find((total) => total.group_key === group?.key)

  if (!group || !groupTotal) return null

  const excludeMixedUnitGroups =
    metricOption.metric === 'quantity' && metricOption.type !== 'percentage'
  const isPercentage =
    metricOption.type === 'percentage' || metricOption.metric === 'damage_severity'

  const categoryTotals = new Map<string, number>()

  for (const row of response.rows) {
    if (row.group_key !== group.key) continue
    if (excludeMixedUnitGroups && row.metric_type === null) continue

    const categoryKey = row.category_key ?? UNCATEGORISED_SERIES_NAME
    categoryTotals.set(categoryKey, (categoryTotals.get(categoryKey) ?? 0) + row.value)
  }

  const categoryKeys = [...categoryTotals.keys()].sort(compareCategoryKeysBySeverity)

  const categories = categoryKeys.map((key) => {
    const value = categoryTotals.get(key) ?? 0
    return {
      key,
      label: key === UNCATEGORISED_SERIES_NAME ? UNCATEGORISED_SERIES_NAME : toDisplayLabel(key),
      value,
      percentOfTotal: groupTotal.total_value > 0 ? (100 * value) / groupTotal.total_value : 0,
    }
  })

  return {
    label: group.label,
    totalValue: isPercentage ? (groupTotal.percent_value ?? 0) : groupTotal.total_value,
    value: groupTotal.value,
    unit: groupTotal.unit,
    categories,
  }
}
